import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  fr: {
    // Navigation
    nav: {
      agency: "L'agence",
      portfolio: "Portfolio",
      baoBrunch: "Services",
      about: "À propos",
      contact: "Contact",
      legal: "Mentions légales",
      cart: "Panier",
      services: "Services",
      boutique: "Boutique",
    },
    // Hero
    hero: {
      label: "BAO AGENCY — Maison Digitale",
      description: "Bienvenue dans notre maison digitale spécialisée en stratégie social media et production de contenu. Nous accompagnons les marques ambitieuses dans la structuration et l'amplification de leur présence digitale.",
      cta: "En savoir plus",
    },
    // About
    about: {
      title: "Qui sommes-nous",
      description: "Bao Agency est une maison digitale spécialisée en stratégie social media et production de contenu. Nous accompagnons les marques et lieux dynamiques dans la structuration, le développement et l'amplification de leur présence digitale. Notre approche repose sur une méthode claire : analyser, structurer, créer et optimiser. Nous ne produisons pas simplement du contenu. Nous construisons des systèmes de visibilité cohérents et alignés aux objectifs business.",
      cta: "En savoir plus",
    },
    // Mission
    mission: {
      title: "Notre Mission",
      description: "Structurer l'image des marques ambitieuses et transformer leur présence digitale en levier de croissance. Clarifier leur positionnement. Construire une identité forte. Développer une stratégie cohérente. Amplifier leur visibilité de manière ciblée.",
    },
    // Brand showcase
    brand: {
      line1: "Montrez votre marque.",
      line2: "Faites briller.",
    },
    // Services
    services: {
      title: "Nos services",
      cta: "Tarif mensuel TTC ( nous contacter )",
      pack1: {
        name: "Pack essentiel",
        price: "1200,00 €",
      },
      pack2: {
        name: "Pack développement",
        price: "1800,00 €",
      },
      pack3: {
        name: "Pack expérience",
        price: "2500,00 €",
      },
    },
    // Cart
    cart: {
      title: "Panier",
      subtotal: "Sous-total",
      checkout: "Paiement",
      empty: "Votre panier est vide",
      remove: "Supprimer",
    },
    // Footer
    footer: {
      explore: "Explorer",
      follow: "Nous suivre",
      newsletter: "Inscrivez-vous avec votre adresse e-mail afin de recevoir les actualités et les mises à jour.",
      emailPlaceholder: "Votre adresse e-mail",
      subscribe: "S'inscrire",
      subscribeSuccess: "Merci pour votre inscription !",
    },
    // Contact
    contact: {
      title: "Contactez-nous",
      name: "Nom",
      email: "Email",
      message: "Message",
      send: "Envoyer",
      success: "Message envoyé avec succès !",
      error: "Erreur lors de l'envoi du message.",
      namePlaceholder: "Votre nom",
      emailPlaceholder: "votre@email.com",
      messagePlaceholder: "Votre message...",
    },
    // Language
    language: "Langue",
  },
  en: {
    // Navigation
    nav: {
      agency: "The Agency",
      portfolio: "Portfolio",
      baoBrunch: "Services",
      about: "About",
      contact: "Contact",
      legal: "Legal Notice",
      cart: "Cart",
      services: "Services",
      boutique: "Shop",
    },
    // Hero
    hero: {
      label: "BAO AGENCY — Digital House",
      description: "Welcome to our digital house specializing in social media strategy and content production. We support ambitious brands in structuring and amplifying their digital presence.",
      cta: "Learn more",
    },
    // About
    about: {
      title: "Who we are",
      description: "Bao Agency is a digital house specializing in social media strategy and content production. We support brands and dynamic venues in structuring, developing and amplifying their digital presence. Our approach is based on a clear method: analyze, structure, create and optimize. We don't just produce content. We build coherent visibility systems aligned with business objectives.",
      cta: "Learn more",
    },
    // Mission
    mission: {
      title: "Our Mission",
      description: "Structure the image of ambitious brands and transform their digital presence into a growth lever. Clarify their positioning. Build a strong identity. Develop a coherent strategy. Amplify their visibility in a targeted way.",
    },
    // Brand showcase
    brand: {
      line1: "Show your brand.",
      line2: "Make it shine.",
    },
    // Services
    services: {
      title: "Our services",
      cta: "Monthly rate incl. tax ( contact us )",
      pack1: {
        name: "Essential Pack",
        price: "€1,200.00",
      },
      pack2: {
        name: "Development Pack",
        price: "€1,800.00",
      },
      pack3: {
        name: "Experience Pack",
        price: "€2,500.00",
      },
    },
    // Cart
    cart: {
      title: "Cart",
      subtotal: "Subtotal",
      checkout: "Checkout",
      empty: "Your cart is empty",
      remove: "Remove",
    },
    // Footer
    footer: {
      explore: "Explore",
      follow: "Follow us",
      newsletter: "Sign up with your email address to receive news and updates.",
      emailPlaceholder: "Your email address",
      subscribe: "Subscribe",
      subscribeSuccess: "Thank you for subscribing!",
    },
    // Contact
    contact: {
      title: "Contact us",
      name: "Name",
      email: "Email",
      message: "Message",
      send: "Send",
      success: "Message sent successfully!",
      error: "Error sending message.",
      namePlaceholder: "Your name",
      emailPlaceholder: "your@email.com",
      messagePlaceholder: "Your message...",
    },
    // Language
    language: "Language",
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => (prev === 'fr' ? 'en' : 'fr'));
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
